import type { UseFormReturnType } from '@mantine/form'
import { notifications } from '@mantine/notifications'

type ApiErrorResponse = {
  response?: {
    data?: unknown
  }
  body?: unknown
}

type CustomFieldItem = {
  field_id?: string | number
  detail?: string
  [key: string]: unknown
}

/**
 * Show API errors in a Mantine notification and set form errors
 */
export const showApiErrors = (
  error: unknown,
  // generic utility: a concrete form type would force every caller's
  // UseFormReturnType<T> to be invariant-compatible, so `any` is intentional here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: UseFormReturnType<any>
): void => {
  let errorData: unknown = undefined

  // 1. Improved Extraction: Handle nested or direct error objects
  if (typeof error === 'object' && error !== null) {
    const err = error as ApiErrorResponse

    // Check response.data, then body, then fallback to the object itself
    errorData = err.response?.data ?? err.body ?? error
  }

  const formErrors: Record<string, string> = {}
  const notificationMessages: string[] = []

  // 2. Flattening Logic
  const flattenErrors = (
    obj: Record<string, unknown>,
    prefix = ''
  ): Record<string, string> => {
    const flattened: Record<string, string> = {}

    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key

      if (Array.isArray(value)) {
        const messages = value.filter((v): v is string => typeof v === 'string')

        if (messages.length > 0) {
          flattened[fullKey] = messages[0]
        }

        value.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            const itemObj = item as Record<string, unknown>

            if (fullKey.endsWith('custom_field_values') && itemObj.field_id) {
              const customItem = itemObj as CustomFieldItem

              if (customItem.detail) {
                flattened[`${fullKey}.${customItem.field_id}`] = String(
                  customItem.detail
                )
              }

              return
            }
            Object.assign(
              flattened,
              flattenErrors(itemObj, `${fullKey}.${index}`)
            )
          }
        })
      } else if (typeof value === 'string') {
        flattened[fullKey] = value
      } else if (typeof value === 'object' && value !== null) {
        Object.assign(
          flattened,
          flattenErrors(value as Record<string, unknown>, fullKey)
        )
      }
    }

    return flattened
  }

  // 3. Process Flattened Data
  if (typeof errorData === 'string') {
    notificationMessages.push(errorData)
  } else if (Array.isArray(errorData)) {
    notificationMessages.push(...errorData.map(String))
  } else if (typeof errorData === 'object' && errorData !== null) {
    const flattenedErrors = flattenErrors(errorData as Record<string, unknown>)

    for (const [fieldPath, message] of Object.entries(flattenedErrors)) {
      // Keys that should always be notifications, never form errors
      const isGlobalKey = [
        'detail',
        'non_field_errors',
        'message',
        'error'
      ].includes(fieldPath)

      // Determine if this path exists in the form values
      const fieldExistsInForm = form ? fieldPath in form.values : false

      if (!isGlobalKey && fieldExistsInForm) {
        formErrors[fieldPath] = message
      } else {
        // Construct notification: "Detail: message" is redundant, so we strip common keys
        const label = isGlobalKey
          ? ''
          : (fieldPath.split('.').pop() ?? fieldPath)
        const displayLabel = label
          ? `${label.charAt(0).toUpperCase() + label.slice(1)}: `
          : ''

        notificationMessages.push(`${displayLabel}${message}`)
      }
    }
  }

  // 4. Update Mantine Form
  if (form && Object.keys(formErrors).length > 0) {
    form.setErrors(formErrors)
  }

  // 5. Show Mantine Notifications
  const shown = new Set<string>()

  notificationMessages.forEach((msg) => {
    if (!shown.has(msg)) {
      shown.add(msg)
      notifications.show({
        title: 'Error',
        message: msg,
        color: 'red',
        autoClose: 3000
      })
    }
  })
}
